import { useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserCog, ShieldCheck, Mail, Wallet, CheckCircle2 } from "lucide-react";
import QuickLoginSettings from "@/components/QuickLoginSettings";
import DeleteAccountSection from "@/components/DeleteAccountSection";

const BEP20_RE = /^0x[a-fA-F0-9]{40}$/;

export default function Profile() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);

  // Wallet section state
  const [wallet, setWallet] = useState(user?.wallet_address || "");
  const [walletOtp, setWalletOtp] = useState("");
  const [walletOtpSent, setWalletOtpSent] = useState(false);
  const [walletBusy, setWalletBusy] = useState(false);
  const walletValid = BEP20_RE.test(wallet.trim());
  const walletChanged = wallet.trim() && wallet.trim() !== (user?.wallet_address || "");

  const request = async () => {
    setBusy(true);
    try {
      await api.post("/profile/request-otp");
      setOtpSent(true);
      toast.success("OTP sent to your registered email");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/profile/update", { name, phone, otp });
      toast.success("Profile updated");
      setOtpSent(false); setOtp("");
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const requestWalletOtp = async () => {
    if (!walletValid) { toast.error("Enter a valid BEP20 (BSC) wallet address first"); return; }
    setWalletBusy(true);
    try {
      await api.post("/profile/request-otp");
      setWalletOtpSent(true);
      toast.success("OTP sent to your registered email");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setWalletBusy(false); }
  };
  const saveWallet = async (e) => {
    e.preventDefault();
    if (!walletValid) { toast.error("Invalid BEP20 address"); return; }
    setWalletBusy(true);
    try {
      await api.post("/profile/wallet", { wallet_address: wallet.trim(), otp: walletOtp });
      toast.success("Wallet address saved. All future withdrawals go here.");
      setWalletOtpSent(false); setWalletOtp("");
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setWalletBusy(false); }
  };

  return (
    <AppLayout variant="user">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-nb-cyan">Account</div>
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mt-1 flex items-center gap-3"><UserCog className="text-[#8AE234]"/> My Profile</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="nb-card p-6">
          <div className="font-heading font-semibold text-white mb-4">Account Info</div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-400">Email</span><span className="text-white font-mono text-xs">{user?.email}</span></div>
            <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-400">Referral Code</span><span className="text-nb-cyan font-mono">{user?.referral_code}</span></div>
            <div className="flex justify-between border-b border-white/5 pb-2 items-start gap-3">
              <span className="text-slate-400">Sponsor / Upline</span>
              <span className="text-right">
                {user?.upline ? (
                  <>
                    <span className="text-white block truncate max-w-[180px]" title={user.upline.name}>{user.upline.name}</span>
                    <span className="text-nb-cyan font-mono text-[11px]">{user.upline.referral_code}</span>
                  </>
                ) : (
                  <span className="text-slate-500 text-xs">Root / no upline</span>
                )}
              </span>
            </div>
            <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-400">Total Team Members</span><span className="text-white font-mono">{user?.total_team_size ?? 0}</span></div>
            <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-400">Rank</span><span className="text-white uppercase">{user?.rank}</span></div>
            <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-400">KYC Status</span><span className="text-white">{user?.kyc_status}</span></div>
            <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-400">Wallet Address</span><span className="text-white font-mono text-xs">{user?.wallet_address ? `${user.wallet_address.slice(0,6)}…${user.wallet_address.slice(-4)}` : "Not set"}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Joined</span><span className="text-white text-xs">{new Date(user?.created_at).toLocaleString()}</span></div>
          </div>
        </div>

        <form onSubmit={save} className="nb-card p-6 space-y-3">
          <div className="font-heading font-semibold text-white mb-2">Update details</div>
          <div className="text-xs text-amber-300/80 flex items-center gap-1"><Mail size={12}/> Every profile change requires email OTP for your security.</div>
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-widest">Full name</label>
            <input data-testid="profile-name" value={name} onChange={(e)=>{setName(e.target.value); setOtpSent(false);}} className="nb-input mt-1" disabled={otpSent}/>
          </div>
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-widest">Phone</label>
            <input data-testid="profile-phone" value={phone} onChange={(e)=>{setPhone(e.target.value); setOtpSent(false);}} className="nb-input mt-1" placeholder="+880 …" disabled={otpSent}/>
          </div>
          {!otpSent ? (
            <button type="button" data-testid="profile-request-otp" onClick={request} disabled={busy} className="nb-btn-primary w-full"><ShieldCheck size={14} className="inline mr-1"/> Send Email OTP</button>
          ) : (
            <>
              <div className="rounded-lg border border-[#8AE234]/30 bg-[#8AE234]/5 px-3 py-2 text-xs text-[#8AE234]">OTP sent to your registered email. Please check inbox &amp; spam.</div>
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-widest">Enter OTP</label>
                <input data-testid="profile-otp" required value={otp} onChange={(e)=>setOtp(e.target.value.replace(/\D/g,""))} maxLength={6} className="nb-input mt-1 font-mono text-2xl tracking-[0.4em] text-center" placeholder="000000"/>
              </div>
              <button data-testid="profile-save" disabled={busy} className="nb-btn-primary w-full">{busy?"Saving…":"Save changes"}</button>
              <button type="button" onClick={()=>{setOtpSent(false); setOtp("");}} className="text-xs text-slate-400 hover:text-white w-full">Cancel</button>
            </>
          )}
        </form>
      </div>

      {/* Wallet Address section — BEP20 only */}
      <form onSubmit={saveWallet} className="nb-card p-6 mt-6 space-y-3" data-testid="wallet-section">
        <div className="flex items-center gap-2">
          <Wallet size={18} className="text-[#8AE234]"/>
          <div className="font-heading font-semibold text-white">Withdrawal Wallet (BEP20 · BSC)</div>
        </div>
        <div className="text-xs text-slate-400">Your USDT withdrawals will always be sent to this wallet. Only <strong className="text-white">BEP20 (BSC)</strong> addresses are accepted — anything else will be rejected on save.</div>
        <div>
          <label className="text-xs text-slate-400 uppercase tracking-widest">BEP20 Wallet Address</label>
          <input
            data-testid="wallet-address-input"
            value={wallet}
            onChange={(e)=>{setWallet(e.target.value); setWalletOtpSent(false); setWalletOtp("");}}
            className={`nb-input mt-1 font-mono ${wallet && !walletValid ? "border-red-500/50" : ""}`}
            placeholder="0x…"
            disabled={walletOtpSent}
            maxLength={64}
          />
          <div className="mt-1 text-xs flex items-center gap-1">
            {wallet.length === 0 && <span className="text-slate-500">Format: 0x followed by 40 hex characters (42 total)</span>}
            {wallet.length > 0 && walletValid && <span className="text-[#8AE234] flex items-center gap-1"><CheckCircle2 size={12}/> Valid BEP20 address</span>}
            {wallet.length > 0 && !walletValid && <span className="text-red-400">Invalid BEP20 format</span>}
          </div>
        </div>
        {!walletOtpSent ? (
          <button
            type="button"
            data-testid="wallet-request-otp"
            onClick={requestWalletOtp}
            disabled={walletBusy || !walletValid || !walletChanged}
            className="nb-btn-primary w-full"
          >
            <ShieldCheck size={14} className="inline mr-1"/> {user?.wallet_address ? "Send OTP to update wallet" : "Send OTP to save wallet"}
          </button>
        ) : (
          <>
            <div className="rounded-lg border border-[#8AE234]/30 bg-[#8AE234]/5 px-3 py-2 text-xs text-[#8AE234]">OTP sent to your registered email. Please check inbox &amp; spam.</div>
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-widest">Enter OTP</label>
              <input data-testid="wallet-otp" required value={walletOtp} onChange={(e)=>setWalletOtp(e.target.value.replace(/\D/g,""))} maxLength={6} className="nb-input mt-1 font-mono text-2xl tracking-[0.4em] text-center" placeholder="000000"/>
            </div>
            <button data-testid="wallet-save" disabled={walletBusy || walletOtp.length < 6} className="nb-btn-primary w-full">{walletBusy?"Saving…":"Save wallet address"}</button>
            <button type="button" onClick={()=>{setWalletOtpSent(false); setWalletOtp("");}} className="text-xs text-slate-400 hover:text-white w-full">Cancel</button>
          </>
        )}
      </form>

      <QuickLoginSettings/>

      <DeleteAccountSection/>
    </AppLayout>
  );
}
